
// stocai_frontend/src/components/HomePage/components/Action/EditGoals.jsx

import React, { useState, useEffect, useRef } from "react";
import concentricArrow from "../../../../assets/concentric_arrow.png";
import meter from "../../../../assets/meter.png";
import penEdit from "../../../../assets/pen.png";
import arrow from "../../../../assets/arrow.png";

// ✅ ADD THIS - Same pattern as MyActionsIntro
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000/api';
const ACTION_PLAN_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

const EditGoals = ({ isOpen, onClose, onSave, pot, objective, userId }) => {
  const [objectiveTitle, setObjectiveTitle] = useState("");
  const [shortTermInitiatives, setShortTermInitiatives] = useState([]);
  const [mediumTermInitiatives, setMediumTermInitiatives] = useState([]);
  const [deletedInitiatives, setDeletedInitiatives] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lineHeight, setLineHeight] = useState(400);

  const lineStartRef = useRef(null);
  const lastArrowRef = useRef(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && objective) {
      setObjectiveTitle(objective.title);
      
      const shortTerm = objective.initiatives
        .filter(init => init.horizon === "short_term")
        .map(init => ({ ...init, isNew: false }));
      
      const mediumTerm = objective.initiatives
        .filter(init => init.horizon === "medium_term")
        .map(init => ({ ...init, isNew: false }));
      
      setShortTermInitiatives(shortTerm);
      setMediumTermInitiatives(mediumTerm);
      setDeletedInitiatives([]);
    }
  }, [isOpen, objective]);

  // ✅ Calculate dynamic line height
  useEffect(() => {
    if (!isOpen || !lineStartRef.current || !lastArrowRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (lineStartRef.current && lastArrowRef.current) {
        try {
          const startRect = lineStartRef.current.getBoundingClientRect();
          const arrowRect = lastArrowRef.current.getBoundingClientRect();
          const calculatedHeight = arrowRect.top - startRect.top + 4;
          setLineHeight(calculatedHeight);
        } catch (error) {
          console.error("Error calculating line height:", error);
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [isOpen, shortTermInitiatives.length, mediumTermInitiatives.length]);

  if (!isOpen || !objective) return null;

  const handleDeleteShortTerm = (initiative) => {
    setShortTermInitiatives(prev => prev.filter(item => item.id !== initiative.id));
    
    if (!initiative.isNew) {
      setDeletedInitiatives(prev => [...prev, initiative.id]);
    }
  };

  const handleDeleteMediumTerm = (initiative) => {
    setMediumTermInitiatives(prev => prev.filter(item => item.id !== initiative.id));
    
    if (!initiative.isNew) {
      setDeletedInitiatives(prev => [...prev, initiative.id]);
    }
  };

  const handleAddShortTerm = () => {
    const tempId = `temp-${Date.now()}`;
    setShortTermInitiatives(prev => [...prev, {
      id: tempId,
      title: "",
      horizon: "short_term",
      xp_value: 5,
      isNew: true
    }]);
  };

  const handleAddMediumTerm = () => {
    const tempId = `temp-${Date.now()}`;
    setMediumTermInitiatives(prev => [...prev, {
      id: tempId,
      title: "",
      horizon: "medium_term",
      xp_value: 10,
      isNew: true
    }]);
  };

  const handleUpdateShortTerm = (id, newText) => {
    setShortTermInitiatives(prev =>
      prev.map(i => i.id === id ? { ...i, title: newText } : i)
    );
  };

  const handleUpdateMediumTerm = (id, newText) => {
    setMediumTermInitiatives(prev =>
      prev.map(i => i.id === id ? { ...i, title: newText } : i)
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const allInitiatives = [...shortTermInitiatives, ...mediumTermInitiatives];
      
      // 1. Update objective title
      if (objectiveTitle !== objective.title) {
        const updateObjectiveResponse = await fetch(
          `${ACTION_PLAN_BASE_URL}/action-plan/${userId}/objectives/${objective.id}`, // ✅ FIXED
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title: objectiveTitle })
          }
        );

        if (!updateObjectiveResponse.ok) {
          throw new Error("Failed to update objective");
        }
      }

      // 2. Create new initiatives
      const newInitiatives = allInitiatives.filter(init => init.isNew && init.title.trim());
      
      for (const init of newInitiatives) {
        const createResponse = await fetch(
          `${ACTION_PLAN_BASE_URL}/action-plan/${userId}/initiatives`, // ✅ FIXED
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              objective_id: objective.id,
              title: init.title,
              horizon: init.horizon,
              xp_value: init.xp_value
            })
          }
        );

        if (!createResponse.ok) {
          throw new Error("Failed to create initiative");
        }
      }

      // 3. Update existing initiatives
      const updatedInitiatives = allInitiatives.filter(init => !init.isNew);
      
      for (const init of updatedInitiatives) {
        const originalInit = objective.initiatives.find(i => i.id === init.id);
        if (originalInit && originalInit.title !== init.title) {
          const updateResponse = await fetch(
            `${ACTION_PLAN_BASE_URL}/action-plan/${userId}/initiatives/${init.id}`, // ✅ FIXED
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ title: init.title })
            }
          );

          if (!updateResponse.ok) {
            throw new Error("Failed to update initiative");
          }
        }
      }

      // 4. Delete removed initiatives
      for (const initId of deletedInitiatives) {
        const deleteResponse = await fetch(
          `${ACTION_PLAN_BASE_URL}/action-plan/${userId}/initiatives/${initId}`, // ✅ FIXED
          {
            method: "DELETE"
          }
        );

        if (!deleteResponse.ok) {
          throw new Error("Failed to delete initiative");
        }
      }

      if (onSave) {
        onSave();
      }

      onClose();
      
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const goalTitle = pot?.title.replace(' Pot', '') + ' Goals' || 'Goals';

  // ✅ Determine which is the last arrow
  const hasShortTerm = shortTermInitiatives.length > 0;
  const hasMediumTerm = mediumTermInitiatives.length > 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000CC]">
      <div className="w-[1189px] h-[904px] bg-white rounded-[20px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="w-full h-[103px] bg-[#259594] flex items-center px-8">
          <h2 className="text-[24.17px] leading-[1.35] font-medium text-white">
            {goalTitle}
          </h2>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-9 py-6">
          {/* Objective Title Section */}
          <div className="mb-6">
            <h3 className="text-[24px] leading-[1.35] font-medium text-[#424242] capitalize mb-4">
              Objective Title
            </h3>

            {/* Objective Input Box */}
            <div className="w-[1109px] h-[84px] border border-[#DBDBDB] rounded-lg flex items-center px-5 relative">
              <img
                src={concentricArrow}
                alt=""
                className="w-[42px] h-[42px] object-contain mr-4"
              />
              
              <input
                type="text"
                value={objectiveTitle}
                onChange={(e) => setObjectiveTitle(e.target.value)}
                className="flex-1 text-[21px] leading-[1.35] font-medium text-[#424242] outline-none"
              />

              <button type="button" className="w-[24px] h-[24px] ml-4">
                <img
                  src={penEdit}
                  alt="Edit"
                  className="w-[24px] h-[24px] object-contain"
                />
              </button>
            </div>
          </div>

          {/* Main Content with Dynamic Arrow */}
          <div className="flex">
            {/* ✅ Left side with dynamic vertical line */}
            <div className="flex-shrink-0 mr-12 ml-2 -mt-6 relative">
              {/* Reference point for line start */}
              <div ref={lineStartRef} className="absolute left-[9px] top-0 w-0 h-0" />
              
              {/* Dynamic vertical line */}
              <div 
                className="absolute left-[9px] top-0 border-l border-[#D1D1D1]"
                style={{ height: `${lineHeight}px` }}
              />
            </div>

            {/* Right Content */}
            <div className="flex-1">
              {/* Short Term Initiatives */}
              {hasShortTerm && (
                <div className="mb-8">
                  <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF] mb-6">
                    Short term initiatives
                  </p>

                  <div className="space-y-4">
                    {shortTermInitiatives.map((item, index) => {
                      const isLastArrow = !hasMediumTerm && index === shortTermInitiatives.length - 1;
                      
                      return (
                        <div key={item.id} className="relative">
                          {/* ✅ Arrow for each initiative */}
                          <img
                            ref={isLastArrow ? lastArrowRef : null}
                            src={arrow}
                            alt=""
                            className="absolute -left-[36px] top-[10px] w-[17.6px] h-auto object-contain"
                          />
                          
                          <div className="flex items-center gap-4">
                            <img
                              src={meter}
                              alt=""
                              className="w-[31px] h-[30px] object-contain flex-shrink-0"
                            />
                            
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => handleUpdateShortTerm(item.id, e.target.value)}
                              className="flex-1 text-[15px] leading-[1.45] font-normal text-black outline-none"
                              placeholder="Enter initiative..."
                            />

                            <span className="text-[12px] leading-[1.1] font-medium uppercase text-[#8C8C8C] text-right">
                              +{item.xp_value}XP
                            </span>

                            <button
                              type="button"
                              onClick={() => handleDeleteShortTerm(item)}
                              className="w-[20px] h-[20px] flex items-center justify-center hover:opacity-70"
                            >
                              <svg
                                className="w-[10px] h-[10px]"
                                viewBox="0 0 10 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 1L9 9M9 1L1 9"
                                  stroke="#C1C1C1"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>

                          {/* Separator line */}
                          <div className="ml-[47px] mt-1 w-[1003px] border-t border-[#E4E4E4]" />
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={handleAddShortTerm}
                    className="mt-4 ml-[47px] text-[15px] leading-[1.45] font-medium text-[#666666] hover:text-[#259594]"
                  >
                    + Add New Initiative
                  </button>
                </div>
              )}

              {/* Medium Term Initiatives */}
              {hasMediumTerm && (
                <div className="mb-8">
                  <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF] mb-6">
                    Medium term initiatives
                  </p>

                  <div className="space-y-4">
                    {mediumTermInitiatives.map((item, index) => {
                      const isLastArrow = index === mediumTermInitiatives.length - 1;
                      
                      return (
                        <div key={item.id} className="relative">
                          {/* ✅ Arrow for each initiative */}
                          <img
                            ref={isLastArrow ? lastArrowRef : null}
                            src={arrow}
                            alt=""
                            className="absolute -left-[36px] top-[10px] w-[17.6px] h-auto object-contain"
                          />
                          
                          <div className="flex items-center gap-4">
                            <img
                              src={meter}
                              alt=""
                              className="w-[31px] h-[30px] object-contain flex-shrink-0"
                            />
                            
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => handleUpdateMediumTerm(item.id, e.target.value)}
                              className="flex-1 text-[15px] leading-[1.45] font-normal text-black outline-none"
                              placeholder="Enter initiative..."
                            />

                            <span className="text-[12px] leading-[1.1] font-medium uppercase text-[#8C8C8C] text-right">
                              +{item.xp_value}XP
                            </span>

                            <button
                              type="button"
                              onClick={() => handleDeleteMediumTerm(item)}
                              className="w-[20px] h-[20px] flex items-center justify-center hover:opacity-70"
                            >
                              <svg
                                className="w-[10px] h-[10px]"
                                viewBox="0 0 10 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 1L9 9M9 1L1 9"
                                  stroke="#C1C1C1"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>

                          {/* Separator line */}
                          <div className="ml-[47px] mt-1 w-[1003px] border-t border-[#E4E4E4]" />
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={handleAddMediumTerm}
                    className="mt-4 ml-[47px] text-[15px] leading-[1.45] font-medium text-[#666666] hover:text-[#259594]"
                  >
                    + Add New Initiative
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full h-[80px] bg-[#EAF6F6] flex items-center justify-between px-8">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="text-[17.27px] leading-[1.35] font-normal text-[#424242] hover:text-black disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="h-[40px] px-[22px] rounded-[5.35px] bg-[#259594] flex items-center justify-center hover:bg-[#1f7b7a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span className="text-[14.69px] leading-[1] font-medium text-white">
              {isSaving ? "Saving..." : "Save Edits"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditGoals;

// // stocai_frontend/src/components/HomePage/components/Action/EditGoals.jsx

// import React, { useState, useEffect, useRef } from "react";
// import concentricArrow from "../../../../assets/concentric_arrow.png";
// import meter from "../../../../assets/meter.png";
// import penEdit from "../../../../assets/pen.png";
// import arrow from "../../../../assets/arrow.png";

// const EditGoals = ({ isOpen, onClose, onSave, pot, objective, userId }) => {
//   const [objectiveTitle, setObjectiveTitle] = useState("");
//   const [shortTermInitiatives, setShortTermInitiatives] = useState([]);
//   const [mediumTermInitiatives, setMediumTermInitiatives] = useState([]);
//   const [deletedInitiatives, setDeletedInitiatives] = useState([]);
//   const [isSaving, setIsSaving] = useState(false);
//   const [lineHeight, setLineHeight] = useState(400);

//   const lineStartRef = useRef(null);
//   const lastArrowRef = useRef(null);

//   // Load data when modal opens
//   useEffect(() => {
//     if (isOpen && objective) {
//       setObjectiveTitle(objective.title);
      
//       const shortTerm = objective.initiatives
//         .filter(init => init.horizon === "short_term")
//         .map(init => ({ ...init, isNew: false }));
      
//       const mediumTerm = objective.initiatives
//         .filter(init => init.horizon === "medium_term")
//         .map(init => ({ ...init, isNew: false }));
      
//       setShortTermInitiatives(shortTerm);
//       setMediumTermInitiatives(mediumTerm);
//       setDeletedInitiatives([]);
//     }
//   }, [isOpen, objective]);

//   // ✅ Calculate dynamic line height
//   useEffect(() => {
//     if (!isOpen || !lineStartRef.current || !lastArrowRef.current) {
//       return;
//     }

//     const timer = setTimeout(() => {
//       if (lineStartRef.current && lastArrowRef.current) {
//         try {
//           const startRect = lineStartRef.current.getBoundingClientRect();
//           const arrowRect = lastArrowRef.current.getBoundingClientRect();
//           const calculatedHeight = arrowRect.top - startRect.top + 4;
//           setLineHeight(calculatedHeight);
//         } catch (error) {
//           console.error("Error calculating line height:", error);
//         }
//       }
//     }, 50);

//     return () => clearTimeout(timer);
//   }, [isOpen, shortTermInitiatives.length, mediumTermInitiatives.length]);

//   if (!isOpen || !objective) return null;

//   const handleDeleteShortTerm = (initiative) => {
//     setShortTermInitiatives(prev => prev.filter(item => item.id !== initiative.id));
    
//     if (!initiative.isNew) {
//       setDeletedInitiatives(prev => [...prev, initiative.id]);
//     }
//   };

//   const handleDeleteMediumTerm = (initiative) => {
//     setMediumTermInitiatives(prev => prev.filter(item => item.id !== initiative.id));
    
//     if (!initiative.isNew) {
//       setDeletedInitiatives(prev => [...prev, initiative.id]);
//     }
//   };

//   const handleAddShortTerm = () => {
//     const tempId = `temp-${Date.now()}`;
//     setShortTermInitiatives(prev => [...prev, {
//       id: tempId,
//       title: "",
//       horizon: "short_term",
//       xp_value: 5,
//       isNew: true
//     }]);
//   };

//   const handleAddMediumTerm = () => {
//     const tempId = `temp-${Date.now()}`;
//     setMediumTermInitiatives(prev => [...prev, {
//       id: tempId,
//       title: "",
//       horizon: "medium_term",
//       xp_value: 10,
//       isNew: true
//     }]);
//   };

//   const handleUpdateShortTerm = (id, newText) => {
//     setShortTermInitiatives(prev =>
//       prev.map(i => i.id === id ? { ...i, title: newText } : i)
//     );
//   };

//   const handleUpdateMediumTerm = (id, newText) => {
//     setMediumTermInitiatives(prev =>
//       prev.map(i => i.id === id ? { ...i, title: newText } : i)
//     );
//   };

//   const handleSave = async () => {
//     try {
//       setIsSaving(true);

//       const allInitiatives = [...shortTermInitiatives, ...mediumTermInitiatives];
      
//       // 1. Update objective title
//       if (objectiveTitle !== objective.title) {
//         const updateObjectiveResponse = await fetch(
//           `http://localhost:8000/action-plan/${userId}/objectives/${objective.id}`,
//           {
//             method: "PATCH",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({ title: objectiveTitle })
//           }
//         );

//         if (!updateObjectiveResponse.ok) {
//           throw new Error("Failed to update objective");
//         }
//       }

//       // 2. Create new initiatives
//       const newInitiatives = allInitiatives.filter(init => init.isNew && init.title.trim());
      
//       for (const init of newInitiatives) {
//         const createResponse = await fetch(
//           `http://localhost:8000/action-plan/${userId}/initiatives`,
//           {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({
//               objective_id: objective.id,
//               title: init.title,
//               horizon: init.horizon,
//               xp_value: init.xp_value
//             })
//           }
//         );

//         if (!createResponse.ok) {
//           throw new Error("Failed to create initiative");
//         }
//       }

//       // 3. Update existing initiatives
//       const updatedInitiatives = allInitiatives.filter(init => !init.isNew);
      
//       for (const init of updatedInitiatives) {
//         const originalInit = objective.initiatives.find(i => i.id === init.id);
//         if (originalInit && originalInit.title !== init.title) {
//           const updateResponse = await fetch(
//             `http://localhost:8000/action-plan/${userId}/initiatives/${init.id}`,
//             {
//               method: "PATCH",
//               headers: { "Content-Type": "application/json" },
//               body: JSON.stringify({ title: init.title })
//             }
//           );

//           if (!updateResponse.ok) {
//             throw new Error("Failed to update initiative");
//           }
//         }
//       }

//       // 4. Delete removed initiatives
//       for (const initId of deletedInitiatives) {
//         const deleteResponse = await fetch(
//           `http://localhost:8000/action-plan/${userId}/initiatives/${initId}`,
//           {
//             method: "DELETE"
//           }
//         );

//         if (!deleteResponse.ok) {
//           throw new Error("Failed to delete initiative");
//         }
//       }

//       if (onSave) {
//         onSave();
//       }

//       onClose();
      
//     } catch (error) {
//       console.error("Error saving changes:", error);
//       alert("Failed to save changes. Please try again.");
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   const goalTitle = pot?.title.replace(' Pot', '') + ' Goals' || 'Goals';

//   // ✅ Determine which is the last arrow
//   const hasShortTerm = shortTermInitiatives.length > 0;
//   const hasMediumTerm = mediumTermInitiatives.length > 0;

//   return (
//     <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000CC]">
//       <div className="w-[1189px] h-[904px] bg-white rounded-[20px] overflow-hidden flex flex-col">
//         {/* Header */}
//         <div className="w-full h-[103px] bg-[#259594] flex items-center px-8">
//           <h2 className="text-[24.17px] leading-[1.35] font-medium text-white">
//             {goalTitle}
//           </h2>
//         </div>

//         {/* Body */}
//         <div className="flex-1 overflow-y-auto px-9 py-6">
//           {/* Objective Title Section */}
//           <div className="mb-6">
//             <h3 className="text-[24px] leading-[1.35] font-medium text-[#424242] capitalize mb-4">
//               Objective Title
//             </h3>

//             {/* Objective Input Box */}
//             <div className="w-[1109px] h-[84px] border border-[#DBDBDB] rounded-lg flex items-center px-5 relative">
//               <img
//                 src={concentricArrow}
//                 alt=""
//                 className="w-[42px] h-[42px] object-contain mr-4"
//               />
              
//               <input
//                 type="text"
//                 value={objectiveTitle}
//                 onChange={(e) => setObjectiveTitle(e.target.value)}
//                 className="flex-1 text-[21px] leading-[1.35] font-medium text-[#424242] outline-none"
//               />

//               <button type="button" className="w-[24px] h-[24px] ml-4">
//                 <img
//                   src={penEdit}
//                   alt="Edit"
//                   className="w-[24px] h-[24px] object-contain"
//                 />
//               </button>
//             </div>
//           </div>

//           {/* Main Content with Dynamic Arrow */}
//           <div className="flex">
//             {/* ✅ Left side with dynamic vertical line */}
//             <div className="flex-shrink-0 mr-12 ml-2 -mt-6 relative">
//               {/* Reference point for line start */}
//               <div ref={lineStartRef} className="absolute left-[9px] top-0 w-0 h-0" />
              
//               {/* Dynamic vertical line */}
//               <div 
//                 className="absolute left-[9px] top-0 border-l border-[#D1D1D1]"
//                 style={{ height: `${lineHeight}px` }}
//               />
//             </div>

//             {/* Right Content */}
//             <div className="flex-1">
//               {/* Short Term Initiatives */}
//               {hasShortTerm && (
//                 <div className="mb-8">
//                   <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF] mb-6">
//                     Short term initiatives
//                   </p>

//                   <div className="space-y-4">
//                     {shortTermInitiatives.map((item, index) => {
//                       const isLastArrow = !hasMediumTerm && index === shortTermInitiatives.length - 1;
                      
//                       return (
//                         <div key={item.id} className="relative">
//                           {/* ✅ Arrow for each initiative */}
//                           <img
//                             ref={isLastArrow ? lastArrowRef : null}
//                             src={arrow}
//                             alt=""
//                             className="absolute -left-[36px] top-[10px] w-[17.6px] h-auto object-contain"
//                           />
                          
//                           <div className="flex items-center gap-4">
//                             <img
//                               src={meter}
//                               alt=""
//                               className="w-[31px] h-[30px] object-contain flex-shrink-0"
//                             />
                            
//                             <input
//                               type="text"
//                               value={item.title}
//                               onChange={(e) => handleUpdateShortTerm(item.id, e.target.value)}
//                               className="flex-1 text-[15px] leading-[1.45] font-normal text-black outline-none"
//                               placeholder="Enter initiative..."
//                             />

//                             <span className="text-[12px] leading-[1.1] font-medium uppercase text-[#8C8C8C] text-right">
//                               +{item.xp_value}XP
//                             </span>

//                             <button
//                               type="button"
//                               onClick={() => handleDeleteShortTerm(item)}
//                               className="w-[20px] h-[20px] flex items-center justify-center hover:opacity-70"
//                             >
//                               <svg
//                                 className="w-[10px] h-[10px]"
//                                 viewBox="0 0 10 10"
//                                 fill="none"
//                                 xmlns="http://www.w3.org/2000/svg"
//                               >
//                                 <path
//                                   d="M1 1L9 9M9 1L1 9"
//                                   stroke="#C1C1C1"
//                                   strokeWidth="1.5"
//                                   strokeLinecap="round"
//                                 />
//                               </svg>
//                             </button>
//                           </div>

//                           {/* Separator line */}
//                           <div className="ml-[47px] mt-1 w-[1003px] border-t border-[#E4E4E4]" />
//                         </div>
//                       );
//                     })}
//                   </div>

//                   <button
//                     type="button"
//                     onClick={handleAddShortTerm}
//                     className="mt-4 ml-[47px] text-[15px] leading-[1.45] font-medium text-[#666666] hover:text-[#259594]"
//                   >
//                     + Add New Initiative
//                   </button>
//                 </div>
//               )}

//               {/* Medium Term Initiatives */}
//               {hasMediumTerm && (
//                 <div className="mb-8">
//                   <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF] mb-6">
//                     Medium term initiatives
//                   </p>

//                   <div className="space-y-4">
//                     {mediumTermInitiatives.map((item, index) => {
//                       const isLastArrow = index === mediumTermInitiatives.length - 1;
                      
//                       return (
//                         <div key={item.id} className="relative">
//                           {/* ✅ Arrow for each initiative */}
//                           <img
//                             ref={isLastArrow ? lastArrowRef : null}
//                             src={arrow}
//                             alt=""
//                             className="absolute -left-[36px] top-[10px] w-[17.6px] h-auto object-contain"
//                           />
                          
//                           <div className="flex items-center gap-4">
//                             <img
//                               src={meter}
//                               alt=""
//                               className="w-[31px] h-[30px] object-contain flex-shrink-0"
//                             />
                            
//                             <input
//                               type="text"
//                               value={item.title}
//                               onChange={(e) => handleUpdateMediumTerm(item.id, e.target.value)}
//                               className="flex-1 text-[15px] leading-[1.45] font-normal text-black outline-none"
//                               placeholder="Enter initiative..."
//                             />

//                             <span className="text-[12px] leading-[1.1] font-medium uppercase text-[#8C8C8C] text-right">
//                               +{item.xp_value}XP
//                             </span>

//                             <button
//                               type="button"
//                               onClick={() => handleDeleteMediumTerm(item)}
//                               className="w-[20px] h-[20px] flex items-center justify-center hover:opacity-70"
//                             >
//                               <svg
//                                 className="w-[10px] h-[10px]"
//                                 viewBox="0 0 10 10"
//                                 fill="none"
//                                 xmlns="http://www.w3.org/2000/svg"
//                               >
//                                 <path
//                                   d="M1 1L9 9M9 1L1 9"
//                                   stroke="#C1C1C1"
//                                   strokeWidth="1.5"
//                                   strokeLinecap="round"
//                                 />
//                               </svg>
//                             </button>
//                           </div>

//                           {/* Separator line */}
//                           <div className="ml-[47px] mt-1 w-[1003px] border-t border-[#E4E4E4]" />
//                         </div>
//                       );
//                     })}
//                   </div>

//                   <button
//                     type="button"
//                     onClick={handleAddMediumTerm}
//                     className="mt-4 ml-[47px] text-[15px] leading-[1.45] font-medium text-[#666666] hover:text-[#259594]"
//                   >
//                     + Add New Initiative
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="w-full h-[80px] bg-[#EAF6F6] flex items-center justify-between px-8">
//           <button
//             type="button"
//             onClick={onClose}
//             disabled={isSaving}
//             className="text-[17.27px] leading-[1.35] font-normal text-[#424242] hover:text-black disabled:opacity-50"
//           >
//             Cancel
//           </button>

//           <button
//             type="button"
//             onClick={handleSave}
//             disabled={isSaving}
//             className="h-[40px] px-[22px] rounded-[5.35px] bg-[#259594] flex items-center justify-center hover:bg-[#1f7b7a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//           >
//             <span className="text-[14.69px] leading-[1] font-medium text-white">
//               {isSaving ? "Saving..." : "Save Edits"}
//             </span>
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EditGoals;